import React, { useState, useEffect } from 'react';
import { useAdminApiService } from '../../hooks/useAdminApiService';
import { useAppSelector } from '../../store/hooks';
import { FaCoins, FaSync, FaExclamationTriangle, FaTh, FaList, FaThLarge, FaTags, FaSave } from 'react-icons/fa';
import ProfitHistoryChart from './ProfitHistoryChart';
import './AdminComponents.css';

interface KucoinBalancesProps {
  className?: string;
}

type LayoutType = 'grid' | 'list' | 'cards' | 'badges';
type AccountType = 'main' | 'trade' | 'margin' | 'futures';

// Cache duration in milliseconds (5 minutes)
const CACHE_DURATION = 5 * 60 * 1000;
// Realised PnL modal pagination (fixed-size paging client-side)
const REALISED_TRADES_PAGE_SIZE = 50;

// Helper functions for cache management
const getCachedData = (key: string) => {
  try {
    const cached = sessionStorage.getItem(key);
    if (cached) {
      const { data, timestamp } = JSON.parse(cached);
      const now = Date.now();
      if (now - timestamp < CACHE_DURATION) {
        console.log(`KucoinBalances: Using cached data for ${key}, age: ${Math.round((now - timestamp) / 1000)}s`);
        return data;
      } else {
        console.log(`KucoinBalances: Cache expired for ${key}, age: ${Math.round((now - timestamp) / 1000)}s`);
        sessionStorage.removeItem(key);
      }
    }
  } catch (error) {
    console.error(`Failed to get cached data for ${key}:`, error);
  }
  return null;
};

const setCachedData = (key: string, data: any) => {
  try {
    sessionStorage.setItem(key, JSON.stringify({
      data,
      timestamp: Date.now()
    }));
    console.log(`KucoinBalances: Cached data for ${key}`);
  } catch (error) {
    console.error(`Failed to cache data for ${key}:`, error);
  }
};

const KucoinBalances: React.FC<KucoinBalancesProps> = ({ className = '' }) => {
  const adminPermissions = useAppSelector((state) => state.auth.adminUser?.permissions || []);
  const canUseSharedKucoin = adminPermissions.includes('USE_SHARED_KUCOIN_ACCOUNT');
  const adminApi = useAdminApiService();
  // null = shared system account; only valid when canUseSharedKucoin is true
  const [selectedAccountId, setSelectedAccountId] = useState<number | null>(null);
  const [primaryConfigId, setPrimaryConfigId] = useState<number | null>(null); // real DB ID of shared system account
  const [personalAccounts, setPersonalAccounts] = useState<Array<{ id: number; name: string }>>([]);
  const [accountsLoading, setAccountsLoading] = useState(false);
  const [allBalances, setAllBalances] = useState<Record<AccountType, Record<string, string>>>(() => {
    const cached = getCachedData('kucoin-balances');
    return cached || {
      main: {},
      trade: {},
      margin: {},
      futures: {}
    };
  });
  const [selectedAccountType, setSelectedAccountType] = useState<AccountType>('trade');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [layout, setLayout] = useState<LayoutType>('grid');
  const [accountOverview, setAccountOverview] = useState<{
    accountEquity: string;
    realisedPNL?: string;
    unrealisedPNL: string;
    marginBalance: string;
    positionMargin: string;
    orderMargin: string;
    frozenFunds: string;
    availableBalance: string;
    availableMargin: string;
    currency: string;
    riskRatio: string;
    maxWithdrawAmount: string;
  } | null>(() => {
    const cached = getCachedData('kucoin-account-overview');
    return cached || null;
  });

  const [fundingHistory, setFundingHistory] = useState<{
    totalFunded: string;
    fundingEntries: any[];
    currency: string;
  } | null>(() => {
    const cached = getCachedData('kucoin-funding-history');
    return cached || null;
  });

  const [fundingLoading, setFundingLoading] = useState(false);
  const [fundingError, setFundingError] = useState<string | null>(null);

  // Realised PnL history positions modal state
  const [showRealisedTradesModal, setShowRealisedTradesModal] = useState(false);
  const [realisedTradesSortColumn, setRealisedTradesSortColumn] = useState<'closeTime' | 'type' | 'strategyName' | 'pnl'>('closeTime');
  const [realisedTradesSortDirection, setRealisedTradesSortDirection] = useState<'asc' | 'desc'>('desc');
  const [realisedTrades, setRealisedTrades] = useState<Array<{
    id?: string;
    symbol?: string;
    type?: string;
    side?: string;
    size?: string;
    pnl?: string;
    tradeFee?: string;
    fundingFee?: string;
    openTime?: number;
    closeTime?: number;
    openPrice?: string;
    closePrice?: string;
    leverage?: number;
    settleCurrency?: string;
    strategyName?: string; // Local strategy name mapped from `position_tracking.position_id`
  }>>([]);
  const [realisedTradesCurrentPage, setRealisedTradesCurrentPage] = useState(1);
  const [realisedTradesLoading, setRealisedTradesLoading] = useState(false);
  const [realisedTradesError, setRealisedTradesError] = useState<string | null>(null);

  // Load personal accounts + primary (shared system) config ID on mount
  useEffect(() => {
    const loadPersonalAccounts = async () => {
      try {
        setAccountsLoading(true);
        const [accountsResponse, primaryIdResponse] = await Promise.all([
          adminApi.getMyTradingAccounts(),
          adminApi.getPrimaryKucoinConfigId(),
        ]);
        if (accountsResponse.success && accountsResponse.data) {
          const accounts = (accountsResponse.data as any[]).map((acc: any) => ({
            id: acc.id,
            name: acc.name as string,
          }));
          setPersonalAccounts(accounts);

          // If this admin cannot use the shared system account, default to their
          // first personal account instead of null (which maps to the shared account).
          if (!canUseSharedKucoin && accounts.length > 0) {
            setSelectedAccountId(accounts[0].id);
          }
        }
        if (primaryIdResponse.success && primaryIdResponse.data != null) {
          setPrimaryConfigId(primaryIdResponse.data as number);
        }
      } catch (err) {
        console.error('Failed to load personal accounts:', err);
      } finally {
        setAccountsLoading(false);
      }
    };
    loadPersonalAccounts();
  }, [adminApi, canUseSharedKucoin]);

  const loadBalances = async (isRefresh = false) => {
    // Build cache key with account ID
    const cacheKey = selectedAccountId != null 
      ? `kucoin-balances-${selectedAccountId}`
      : 'kucoin-balances';
    const overviewCacheKey = selectedAccountId != null
      ? `kucoin-account-overview-${selectedAccountId}`
      : 'kucoin-account-overview';
    
    // Check cache first (unless it's a manual refresh)
    if (!isRefresh) {
      const cachedBalances = getCachedData(cacheKey);
      const cachedOverview = getCachedData(overviewCacheKey);
      
      if (cachedBalances && cachedOverview) {
        console.log('KucoinBalances: Using cached data, skipping API call', { accountId: selectedAccountId });
        setAllBalances(cachedBalances);
        setAccountOverview(cachedOverview);
        setLastUpdated(new Date());
        setLoading(false);
        return;
      }
    }

    console.log('KucoinBalances: Making API call', { isRefresh, accountId: selectedAccountId });
    setLoading(true);
    setError(null);
    try {
      const [balancesResponse, overviewResponse] = await Promise.all([
        adminApi.getKucoinBalances(selectedAccountId),
        adminApi.getKucoinAccountOverview('USDT', selectedAccountId)
      ]);
      
      if (balancesResponse.success && balancesResponse.data) {
        setAllBalances(balancesResponse.data);
        setLastUpdated(new Date());
        
        // Cache the balances with account-specific key
        setCachedData(cacheKey, balancesResponse.data);
      } else {
        setError(balancesResponse.error || 'Failed to load balances');
      }
      
      if (overviewResponse.success && overviewResponse.data) {
        setAccountOverview(overviewResponse.data);
        
        // Cache the account overview with account-specific key
        setCachedData(overviewCacheKey, overviewResponse.data);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load Kucoin balances');
    } finally {
      setLoading(false);
    }
  };

  // Separate function to load funding history (manual trigger only)
  const loadFundingHistory = async (isRefresh = false) => {
    // Check cache first (unless it's a manual refresh)
    if (!isRefresh) {
      const cachedFunding = getCachedData('kucoin-funding-history');
      if (cachedFunding) {
        console.log('KucoinBalances: Using cached funding history');
        setFundingHistory(cachedFunding);
        return;
      }
    }

    console.log('KucoinBalances: Fetching funding history', { isRefresh });
    setFundingLoading(true);
    setFundingError(null);
    
    try {
      const fundingHistoryResponse = await adminApi.getKucoinFundingHistory('USDT');
      
      if (fundingHistoryResponse.success && fundingHistoryResponse.data) {
        const totalFunded = parseFloat(fundingHistoryResponse.data.totalFunded || '0');
        const hasEntries = fundingHistoryResponse.data.fundingEntries && 
                          fundingHistoryResponse.data.fundingEntries.length > 0;
        
        // Check if feature might be disabled (totalFunded is 0 and no entries)
        // This could mean the flag is disabled, but we still show the data
        if (totalFunded === 0 && !hasEntries && isRefresh) {
          setFundingError('Funding history returned zero. Check if kucoin.funding.history.enabled is enabled in settings.');
        } else {
          setFundingError(null);
        }
        
        setFundingHistory(fundingHistoryResponse.data);
        setCachedData('kucoin-funding-history', fundingHistoryResponse.data);
      } else {
        setFundingError(fundingHistoryResponse.error || 'Failed to load funding history');
      }
    } catch (err: any) {
      setFundingError(err.message || 'Failed to load funding history');
      console.error('Error loading funding history:', err);
    } finally {
      setFundingLoading(false);
    }
  };

  // Load history positions for realised PnL modal
  const loadRealisedTrades = async () => {
    setRealisedTradesLoading(true);
    setRealisedTradesError(null);
    setRealisedTradesCurrentPage(1);
    
    try {
      const response = await adminApi.getKucoinFuturesHistoryPositions(200, selectedAccountId);
      
      if (response.success && response.data) {
        // Sort by closeTime descending (most recent first)
        const sorted = [...response.data].sort((a, b) => {
          const timeA = a.closeTime || 0;
          const timeB = b.closeTime || 0;
          return timeB - timeA;
        });
        setRealisedTrades(sorted);
      } else {
        setRealisedTradesError(response.error || 'Failed to load history positions');
      }
    } catch (err: any) {
      setRealisedTradesError(err.message || 'Failed to load history positions');
      console.error('Error loading history positions:', err);
    } finally {
      setRealisedTradesLoading(false);
    }
  };

  // Load history positions when modal opens
  useEffect(() => {
    if (showRealisedTradesModal) {
      loadRealisedTrades();
    }
  }, [showRealisedTradesModal, selectedAccountId]);

  useEffect(() => {
    loadBalances(false); // Check cache on mount
    // Auto-refresh every 2 minutes (force refresh)
    const interval = setInterval(() => loadBalances(true), 120000);
    return () => clearInterval(interval);
  }, [selectedAccountId]); // Reload when account selection changes

  const formatBalance = (balance: string): string => {
    const num = parseFloat(balance);
    if (isNaN(num)) return balance;
    
    // For very small numbers, show more decimal places
    if (num < 0.0001) {
      return num.toFixed(8);
    }
    // For small numbers, show 4 decimal places
    if (num < 1) {
      return num.toFixed(4);
    }
    // For larger numbers, show 2 decimal places
    return num.toFixed(2);
  };

  const getRealisedTradesStrategyLabel = (trade: { strategyName?: string }) => trade.strategyName || 'Strategy Deleted';
  const getRealisedTradesSortableCloseTime = (t: { closeTime?: number }) => {
    const n = typeof t.closeTime === 'number' ? t.closeTime : 0;
    return Number.isFinite(n) ? n : 0;
  };
  const getRealisedTradesSortableType = (t: { type?: string }) => (t.type || '').toUpperCase();
  const getRealisedTradesSortableStrategy = (t: { strategyName?: string }) => getRealisedTradesStrategyLabel(t).toUpperCase();
  const getRealisedTradesSortablePnl = (t: { pnl?: string }) => {
    const n = parseFloat(t.pnl || '0');
    return Number.isFinite(n) ? n : 0;
  };

  const handleRealisedTradesSort = (column: 'closeTime' | 'type' | 'strategyName' | 'pnl') => {
    if (realisedTradesSortColumn === column) {
      setRealisedTradesSortDirection(d => (d === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setRealisedTradesSortColumn(column);
    setRealisedTradesSortDirection(column === 'pnl' || column === 'closeTime' ? 'desc' : 'asc');
  };

  const realisedTradesSorted = [...realisedTrades].sort((a, b) => {
    let cmp = 0;
    switch (realisedTradesSortColumn) {
      case 'closeTime': {
        cmp = getRealisedTradesSortableCloseTime(a) - getRealisedTradesSortableCloseTime(b);
        break;
      }
      case 'type': {
        cmp = getRealisedTradesSortableType(a).localeCompare(getRealisedTradesSortableType(b));
        break;
      }
      case 'strategyName': {
        cmp = getRealisedTradesSortableStrategy(a).localeCompare(getRealisedTradesSortableStrategy(b));
        break;
      }
      case 'pnl':
      default: {
        cmp = getRealisedTradesSortablePnl(a) - getRealisedTradesSortablePnl(b);
        break;
      }
    }
    return realisedTradesSortDirection === 'asc' ? cmp : -cmp;
  });

  const realisedTradesTotalPages = Math.max(
    1,
    Math.ceil(realisedTradesSorted.length / REALISED_TRADES_PAGE_SIZE)
  );
  const realisedTradesStartIndex =
    (realisedTradesCurrentPage - 1) * REALISED_TRADES_PAGE_SIZE;
  const realisedTradesPaged = realisedTradesSorted.slice(
    realisedTradesStartIndex,
    realisedTradesStartIndex + REALISED_TRADES_PAGE_SIZE
  );

  // Get balances for selected account type
  const balances = allBalances[selectedAccountType] || {};
  
  // Sort balances: USDT first, then by value (descending)
  const sortedBalances = Object.entries(balances).sort(([coinA, balanceA], [coinB, balanceB]) => {
    if (coinA === 'USDT') return -1;
    if (coinB === 'USDT') return 1;
    const valA = parseFloat(balanceA);
    const valB = parseFloat(balanceB);
    return valB - valA;
  });

  // Calculate USDT totals for each account type (only USDT balance, not all coins)
  const getUSDTBalance = (accountBalances: Record<string, string>): number => {
    const usdtBalance = accountBalances['USDT'];
    if (!usdtBalance) return 0;
    const num = parseFloat(usdtBalance);
    return isNaN(num) ? 0 : num;
  };

  const accountTotals: Record<AccountType, number> = {
    main: getUSDTBalance(allBalances.main),
    trade: getUSDTBalance(allBalances.trade),
    margin: getUSDTBalance(allBalances.margin),
    futures: getUSDTBalance(allBalances.futures)
  };

  const totalAllAccounts = accountTotals.main + accountTotals.trade + accountTotals.margin + accountTotals.futures;

  const accountTypeLabels: Record<AccountType, string> = {
    main: 'Funding (Main)',
    trade: 'Trading (Spot)',
    margin: 'Margin',
    futures: 'Futures'
  };

  // NOTE: Strict tab-level permission model:
  // Each account-type tab is visible only when its exact permission is granted.
  const canViewFundingTab = adminPermissions.includes('VIEW_ADMIN_TRADING_SPOT_COINS_FUNDING_TAB');
  const canViewTradingTab = adminPermissions.includes('VIEW_ADMIN_TRADING_SPOT_COINS_TRADING_TAB');
  const canViewMarginTab = adminPermissions.includes('VIEW_ADMIN_TRADING_SPOT_COINS_MARGIN_TAB');
  const canViewFuturesTab = adminPermissions.includes('VIEW_ADMIN_TRADING_SPOT_COINS_FUTURES_TAB');

  const visibleAccountTypes = (Object.keys(accountTypeLabels) as AccountType[]).filter((accountType) => {
    if (accountType === 'main') return canViewFundingTab;
    if (accountType === 'trade') return canViewTradingTab;
    if (accountType === 'margin') return canViewMarginTab;
    if (accountType === 'futures') return canViewFuturesTab;
    return false;
  });

  useEffect(() => {
    if (visibleAccountTypes.length === 0) return;
    if (!visibleAccountTypes.includes(selectedAccountType)) {
      setSelectedAccountType(visibleAccountTypes[0]);
    }
  }, [selectedAccountType, visibleAccountTypes]);

  // Render Grid Layout (Card Grid)
  const renderGridLayout = () => (
    <div className="balances-grid-layout">
      {sortedBalances.map(([coin, balance]) => {
        const isUSDT = coin === 'USDT';
        const numBalance = parseFloat(balance);
        return (
          <div
            key={coin}
            className={`balance-grid-card ${isUSDT ? 'usdt-card' : ''}`}
          >
            <div className="grid-card-header">
              <div className="coin-icon-placeholder">{coin.charAt(0)}</div>
              <div className="coin-info">
                <span className="coin-symbol">{coin}</span>
                {isUSDT && <span className="usdt-badge">Primary</span>}
              </div>
            </div>
            <div className="grid-card-balance">
              <span className={isUSDT ? 'usdt-amount' : ''}>
                {formatBalance(balance)}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );

  // Render List Layout (Enhanced Table)
  const renderListLayout = () => (
    <div className="balances-list-layout">
      <div className="balances-header">
        <div className="balance-coin">Coin</div>
        <div className="balance-amount">Balance</div>
      </div>
      {sortedBalances.map(([coin, balance]) => {
        const isUSDT = coin === 'USDT';
        return (
          <div
            key={coin}
            className={`balance-row ${isUSDT ? 'usdt-row' : ''}`}
          >
            <div className="balance-coin">
              <span className="coin-icon-small">{coin.charAt(0)}</span>
              <span className="coin-symbol">{coin}</span>
            </div>
            <div className="balance-amount">
              <span className={isUSDT ? 'usdt-amount' : ''}>
                {formatBalance(balance)}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );

  // Render Cards Layout (Large Cards)
  const renderCardsLayout = () => (
    <div className="balances-cards-layout">
      {sortedBalances.map(([coin, balance]) => {
        const isUSDT = coin === 'USDT';
        return (
          <div
            key={coin}
            className={`balance-card-large ${isUSDT ? 'usdt-card-large' : ''}`}
          >
            <div className="card-large-content">
              <div className="card-large-icon">
                <div className="coin-icon-large">{coin.charAt(0)}</div>
              </div>
              <div className="card-large-details">
                <div className="card-large-coin">{coin}</div>
                <div className={`card-large-balance ${isUSDT ? 'usdt-amount' : ''}`}>
                  {formatBalance(balance)}
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );

  // Render Badges Layout (Compact Chips)
  const renderBadgesLayout = () => (
    <div className="balances-badges-layout">
      {sortedBalances.map(([coin, balance]) => {
        const isUSDT = coin === 'USDT';
        return (
          <div
            key={coin}
            className={`balance-badge ${isUSDT ? 'usdt-badge-item' : ''}`}
            title={`${coin}: ${formatBalance(balance)}`}
          >
            <span className="badge-coin">{coin}</span>
            <span className={`badge-balance ${isUSDT ? 'usdt-amount' : ''}`}>
              {formatBalance(balance)}
            </span>
          </div>
        );
      })}
    </div>
  );

  return (
    <div 
      className={`kucoin-balances-card ${className}`}
      style={{
        maxWidth: '100%',
        overflowX: 'hidden',
        boxSizing: 'border-box'
      }}
    >
      <div className="card-header">
        <div className="card-title">
          <FaCoins style={{ color: '#e7038e', fontSize: '20px', marginRight: '8px' }} />
          <h3>Kucoin Account Balances</h3>
        </div>
        <div className="card-actions" style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', maxWidth: '100%' }}>
          {/* Account Selector Dropdown */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', flex: '1 1 180px', minWidth: 0, maxWidth: '100%' }}>
            <label htmlFor="account-selector" style={{ fontSize: '12px', color: '#6c757d', fontWeight: 500 }}>
              Account:
            </label>
            <select
              id="account-selector"
              value={selectedAccountId ?? ''}
              onChange={(e) => {
                const value = e.target.value;
                setSelectedAccountId(value === '' ? null : parseInt(value, 10));
              }}
              disabled={accountsLoading}
              style={{
                padding: '6px 12px',
                border: '1px solid #e9ecef',
                borderRadius: '6px',
                fontSize: '13px',
                fontFamily: 'inherit',
                backgroundColor: 'white',
                color: '#212529',
                cursor: accountsLoading ? 'not-allowed' : 'pointer',
                minWidth: 0,
                width: '100%',
                maxWidth: '100%',
                flex: '1 1 140px',
                opacity: accountsLoading ? 0.6 : 1,
                boxSizing: 'border-box',
              }}
            >
              {canUseSharedKucoin && (
                <option value="">Shared KuCoin (System)</option>
              )}
              {personalAccounts.map((account) => (
                <option key={account.id} value={account.id}>
                  {account.name}
                </option>
              ))}
            </select>
          </div>
          {/* Layout Switcher */}
          <div className="layout-switcher">
            <button
              className={`layout-btn ${layout === 'grid' ? 'active' : ''}`}
              onClick={() => setLayout('grid')}
              title="Grid View"
            >
              <FaTh />
            </button>
            <button
              className={`layout-btn ${layout === 'list' ? 'active' : ''}`}
              onClick={() => setLayout('list')}
              title="List View"
            >
              <FaList />
            </button>
            <button
              className={`layout-btn ${layout === 'cards' ? 'active' : ''}`}
              onClick={() => setLayout('cards')}
              title="Cards View"
            >
              <FaThLarge />
            </button>
            <button
              className={`layout-btn ${layout === 'badges' ? 'active' : ''}`}
              onClick={() => setLayout('badges')}
              title="Badges View"
            >
              <FaTags />
            </button>
          </div>
          <button
            className="refresh-btn"
            onClick={() => loadBalances(true)}
            disabled={loading}
            title="Refresh balances"
          >
            <FaSync className={loading ? 'spinning' : ''} />
          </button>
        </div>
      </div>

      {error && (
        <div className="error-message">
          <FaExclamationTriangle style={{ marginRight: '8px' }} />
          {error}
        </div>
      )}

      {loading && !balances && (
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading balances...</p>
        </div>
      )}


      {!loading && (
        <>
          {visibleAccountTypes.length === 0 ? (
            <div className="empty-state">
              <p>You do not have permission to view any KuCoin balance tabs.</p>
            </div>
          ) : (
            <>
          {/* Account Type Tabs */}
          <div className="kucoin-account-tabs" style={{ 
            display: 'flex'
          }}>
            {visibleAccountTypes.map((accountType) => (
              <button
                key={accountType}
                onClick={() => setSelectedAccountType(accountType)}
                style={{
                  padding: '8px 12px',
                  border: 'none',
                  background: selectedAccountType === accountType ? '#e7038e' : 'transparent',
                  color: selectedAccountType === accountType ? 'white' : '#6c757d',
                  borderRadius: '4px 4px 0 0',
                  cursor: 'pointer',
                  fontWeight: selectedAccountType === accountType ? 600 : 400,
                  fontSize: '13px',
                  transition: 'all 0.2s',
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                  minWidth: 'fit-content'
                }}
              >
                <span>{accountTypeLabels[accountType]}</span>
                {accountTotals[accountType] > 0 && (
                  <span style={{ marginLeft: '4px', opacity: 0.8, fontSize: '11px' }}>
                    ({formatBalance(accountTotals[accountType].toString())})
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* Total Summary */}
          <div className="kucoin-total-summary" style={{
            background: '#f8f9fa',
            border: '1px solid #e9ecef',
            borderRadius: '8px',
            padding: '12px 16px',
            marginBottom: '16px'
          }}>
            <div style={{ marginBottom: '8px' }}>
              <span style={{ fontSize: '12px', color: '#6c757d', display: 'block', marginBottom: '4px' }}>
                Total Across All Accounts:
              </span>
              <span style={{ fontSize: '18px', fontWeight: 600, color: '#212529', wordBreak: 'break-word' }}>
                {formatBalance(totalAllAccounts.toString())} USDT
              </span>
            </div>
            <div style={{ 
              display: 'flex', 
              flexWrap: 'wrap',
              gap: '12px', 
              fontSize: '11px', 
              color: '#6c757d',
              marginTop: '8px'
            }}>
              <span>Funding: {formatBalance(accountTotals.main.toString())}</span>
              <span>Trading: {formatBalance(accountTotals.trade.toString())}</span>
              <span>Margin: {formatBalance(accountTotals.margin.toString())}</span>
              <span>Futures: {formatBalance(accountTotals.futures.toString())}</span>
            </div>
          </div>

          {/* Account Overview Section (Futures) */}
          {accountOverview && selectedAccountType === 'futures' && (
            <div className="account-overview-section" style={{
              background: '#f8f9fa',
              border: '1px solid #e9ecef',
              borderRadius: '8px',
              padding: '16px',
              marginBottom: '16px'
            }}>
              <div className="account-overview-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <h4 style={{ margin: 0, fontSize: '14px', fontWeight: 600, color: '#212529' }}>
                  Account Overview (Futures)
                </h4>
                <button
                  onClick={async () => {
                    try {
                      await adminApi.saveKucoinSnapshot();
                      alert('Snapshot saved successfully!');
                    } catch (err) {
                      alert('Failed to save snapshot');
                    }
                  }}
                  style={{
                    padding: '6px 12px',
                    background: '#086afb',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '12px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                  title="Save current account state to history"
                >
                  <FaSave /> Save Snapshot
                </button>
              </div>
              <div className="account-overview-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <span style={{ fontSize: '11px', color: '#6c757d', marginBottom: '4px' }}>Account Equity</span>
                  <span style={{ fontSize: '14px', fontWeight: 600, color: '#212529' }}>
                    {formatBalance(accountOverview.accountEquity)} {accountOverview.currency}
                  </span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <span style={{ fontSize: '11px', color: '#6c757d', marginBottom: '4px' }}>Unrealised PNL</span>
                  <span style={{ 
                    fontSize: '14px', 
                    fontWeight: 600, 
                    color: parseFloat(accountOverview.unrealisedPNL) >= 0 ? '#28a745' : '#dc3545'
                  }}>
                    {parseFloat(accountOverview.unrealisedPNL) >= 0 ? '+' : ''}{formatBalance(accountOverview.unrealisedPNL)} {accountOverview.currency}
                  </span>
                </div>
                {typeof accountOverview.realisedPNL !== 'undefined' && (
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '4px' }}>
                      <span style={{ fontSize: '11px', color: '#6c757d' }}>Realised PNL</span>
                      <span 
                        onClick={() => setShowRealisedTradesModal(true)}
                        style={{ 
                          fontSize: '9px', 
                          color: '#3b82f6',
                          cursor: 'pointer',
                          textDecoration: 'underline',
                          textDecorationStyle: 'dotted'
                        }}
                        title="Click to view last 200 closed positions (history-positions endpoint)"
                      >
                        ℹ️
                      </span>
                    </div>
                    <span style={{
                      fontSize: '14px',
                      fontWeight: 600,
                      color: parseFloat(accountOverview.realisedPNL || '0') >= 0 ? '#28a745' : '#dc3545'
                    }}>
                      {(() => {
                        const realised = parseFloat(accountOverview.realisedPNL || '0');
                        const prefix = realised >= 0 ? '+' : '';
                        return `${prefix}${formatBalance((realised || 0).toString())} ${accountOverview.currency}`;
                      })()}
                    </span>
                    <span style={{ fontSize: '9px', color: '#9ca3af', marginTop: '2px', fontStyle: 'italic' }}>
                      (Last 200 closes)
                    </span>
                  </div>
                )}
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <span style={{ fontSize: '11px', color: '#6c757d', marginBottom: '4px' }}>Margin Balance</span>
                  <span style={{ fontSize: '14px', fontWeight: 600, color: '#212529' }}>
                    {formatBalance(accountOverview.marginBalance)} {accountOverview.currency}
                  </span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <span style={{ fontSize: '11px', color: '#6c757d', marginBottom: '4px' }}>Current Profit</span>
                  <span style={{ 
                    fontSize: '14px', 
                    fontWeight: 600, 
                    color: (parseFloat(accountOverview.unrealisedPNL) + parseFloat(accountOverview.marginBalance)) >= 0 ? '#28a745' : '#dc3545'
                  }}>
                    {(() => {
                      const currentProfit = parseFloat(accountOverview.unrealisedPNL) + parseFloat(accountOverview.marginBalance);
                      return (currentProfit >= 0 ? '+' : '') + formatBalance(currentProfit.toString()) + ' ' + accountOverview.currency;
                    })()}
                  </span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <span style={{ fontSize: '11px', color: '#6c757d', marginBottom: '4px' }}>Position Margin</span>
                  <span style={{ fontSize: '14px', fontWeight: 600, color: '#212529' }}>
                    {formatBalance(accountOverview.positionMargin)} {accountOverview.currency}
                  </span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <span style={{ fontSize: '11px', color: '#6c757d', marginBottom: '4px' }}>Available Balance</span>
                  <span style={{ fontSize: '14px', fontWeight: 600, color: '#28a745' }}>
                    {formatBalance(accountOverview.availableBalance)} {accountOverview.currency}
                  </span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <span style={{ fontSize: '11px', color: '#6c757d', marginBottom: '4px' }}>Available Margin</span>
                  <span style={{ fontSize: '14px', fontWeight: 600, color: '#28a745' }}>
                    {formatBalance(accountOverview.availableMargin)} {accountOverview.currency}
                  </span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <span style={{ fontSize: '11px', color: '#6c757d', marginBottom: '4px' }}>Risk Ratio</span>
                  <span style={{ fontSize: '14px', fontWeight: 600, color: '#212529' }}>
                    {(parseFloat(accountOverview.riskRatio) * 100).toFixed(2)}%
                  </span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <span style={{ fontSize: '11px', color: '#6c757d', marginBottom: '4px' }}>Max Withdraw</span>
                  <span style={{ fontSize: '14px', fontWeight: 600, color: '#212529' }}>
                    {formatBalance(accountOverview.maxWithdrawAmount)} {accountOverview.currency}
                  </span>
                </div>
                {/* Total Funded and ROI Section with Manual Refresh */}
                <div style={{ display: 'flex', flexDirection: 'column', gridColumn: 'span 2' }}>
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                    marginBottom: '8px'
                  }}>
                    <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '8px',
                        marginBottom: '4px'
                      }}>
                        <span style={{ fontSize: '11px', color: '#6c757d' }}>💰 Total Funded</span>
                        <button
                          type="button"
                          onClick={() => loadFundingHistory(true)}
                          disabled={fundingLoading}
                          style={{
                            border: 'none',
                            background: 'transparent',
                            padding: '2px 6px',
                            cursor: fundingLoading ? 'wait' : 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            color: '#086afb',
                            fontSize: '10px',
                            borderRadius: '4px',
                            transition: 'background-color 0.2s',
                            opacity: fundingLoading ? 0.6 : 1
                          }}
                          onMouseEnter={(e) => {
                            if (!fundingLoading) {
                              e.currentTarget.style.backgroundColor = '#f0f0f0';
                            }
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }}
                          title="Refresh Total Funded & ROI"
                        >
                          <FaSync 
                            className={fundingLoading ? 'spinning' : ''}
                            style={{
                              fontSize: '10px'
                            }}
                          />
                          <span>{fundingLoading ? 'Fetching...' : 'Refresh'}</span>
                        </button>
                      </div>
                      <span style={{ 
                        fontSize: '9px', 
                        color: '#6c757d', 
                        fontStyle: 'italic',
                        marginTop: '-2px',
                        marginBottom: '2px'
                      }}>
                        (Last 120 days)
                      </span>
                      {fundingError && (
                        <span style={{ 
                          fontSize: '10px', 
                          color: '#dc3545',
                          marginTop: '2px',
                          display: 'block'
                        }}>
                          {fundingError}
                        </span>
                      )}
                      <span style={{ 
                        fontSize: '14px', 
                        fontWeight: 600, 
                        color: '#086afb',
                        minHeight: '20px',
                        display: 'flex',
                        alignItems: 'center'
                      }}>
                        {fundingLoading && !fundingHistory ? (
                          <span style={{ fontSize: '12px', color: '#6c757d', fontStyle: 'italic' }}>
                            Loading...
                          </span>
                        ) : fundingHistory ? (
                          `${formatBalance(fundingHistory.totalFunded)} ${fundingHistory.currency}`
                        ) : (
                          <span style={{ fontSize: '12px', color: '#6c757d', fontStyle: 'italic' }}>
                            Click refresh to calculate
                          </span>
                        )}
                      </span>
                    </div>
                  </div>
                  
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <span style={{ fontSize: '11px', color: '#6c757d', marginBottom: '4px' }}>📈 ROI%</span>
                    <span style={{ 
                      fontSize: '14px', 
                      fontWeight: 600, 
                      color: (() => {
                        if (!fundingHistory || !accountOverview) return '#6c757d';
                        const funded = parseFloat(fundingHistory.totalFunded);
                        const currentProfit = parseFloat(accountOverview.unrealisedPNL) + parseFloat(accountOverview.marginBalance);
                        const profitAmount = currentProfit - funded;
                        return profitAmount >= 0 ? '#28a745' : '#dc3545';
                      })(),
                      minHeight: '20px',
                      display: 'flex',
                      alignItems: 'center'
                    }}>
                      {fundingLoading && !fundingHistory ? (
                        <span style={{ fontSize: '12px', color: '#6c757d', fontStyle: 'italic' }}>
                          Loading...
                        </span>
                      ) : fundingHistory && accountOverview ? (
                        (() => {
                          const funded = parseFloat(fundingHistory.totalFunded);
                          if (funded === 0) return 'N/A';
                          const currentProfit = parseFloat(accountOverview.unrealisedPNL) + parseFloat(accountOverview.marginBalance);
                          const profitAmount = currentProfit - funded;
                          const roi = (profitAmount / funded) * 100;
                          return (roi >= 0 ? '+' : '') + roi.toFixed(2) + '%';
                        })()
                      ) : (
                        <span style={{ fontSize: '12px', color: '#6c757d', fontStyle: 'italic' }}>
                          N/A
                        </span>
                      )}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}
          {sortedBalances.length > 0 ? (
            <>
              <div className="balances-summary">
                <div className="summary-item">
                  <span className="summary-label">Total Coins ({accountTypeLabels[selectedAccountType]}):</span>
                  <span className="summary-value">{sortedBalances.length}</span>
                </div>
                {balances['USDT'] && (
                  <div className="summary-item">
                    <span className="summary-label">USDT Balance:</span>
                    <span className="summary-value highlight">
                      {formatBalance(balances['USDT'])} USDT
                    </span>
                  </div>
                )}
              </div>

              {/* Render selected layout */}
              {layout === 'grid' && renderGridLayout()}
              {layout === 'list' && renderListLayout()}
              {layout === 'cards' && renderCardsLayout()}
              {layout === 'badges' && renderBadgesLayout()}
            </>
          ) : (
            <div className="empty-state">
              <p>No balances found in {accountTypeLabels[selectedAccountType]} account</p>
            </div>
          )}
            </>
          )}
        </>
      )}

      {lastUpdated && (
        <div className="card-footer">
          <span className="last-updated">
            Last updated: {lastUpdated.toLocaleTimeString()}
          </span>
        </div>
      )}

      {/* Profit History Chart - Only show for Futures account.
          When "Shared KuCoin (System)" is selected (selectedAccountId = null),
          resolve to the primary account's real config ID so the chart shows
          only that account's data. The "Consolidated" view is a toggle on
          the chart itself. */}
      {selectedAccountType === 'futures' && (
        <ProfitHistoryChart
          accountId={selectedAccountId ?? primaryConfigId}
          accountName={
            selectedAccountId != null
              ? (personalAccounts.find(a => a.id === selectedAccountId)?.name ?? `Account #${selectedAccountId}`)
              : 'Shared KuCoin (System)'
          }
        />
      )}

      {/* Realised PnL History Positions Modal */}
      {showRealisedTradesModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: window.innerWidth <= 768 ? 'flex-start' : 'center',
            justifyContent: 'center',
            zIndex: 10000,
            padding: window.innerWidth <= 768 ? '0' : '20px',
            overflowY: 'auto',
            WebkitOverflowScrolling: 'touch'
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowRealisedTradesModal(false);
            }
          }}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: window.innerWidth <= 768 ? '0' : '8px',
              width: '100%',
              maxWidth: '1200px',
              maxHeight: window.innerWidth <= 768 ? '100dvh' : '90vh',
              height: window.innerWidth <= 768 ? 'auto' : 'auto',
              display: 'flex',
              flexDirection: 'column',
              overflow: window.innerWidth <= 768 ? 'visible' : 'hidden',
              boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)',
              minHeight: 0
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div
              style={{
                padding: window.innerWidth <= 768 ? '16px' : '20px',
                borderBottom: '1px solid #e5e7eb',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}
            >
              <h3 style={{ margin: 0, fontSize: window.innerWidth <= 768 ? '16px' : '18px', fontWeight: 600 }}>
                Realised PnL - Last 200 Closed Positions
              </h3>
              <button
                onClick={() => setShowRealisedTradesModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#6b7280',
                  padding: '4px 8px'
                }}
              >
                ×
              </button>
            </div>

            {/* Modal Content */}
            <div
              style={{
                padding: window.innerWidth <= 768 ? '12px' : '20px',
                overflowY: 'auto',
                flex: 1,
                minHeight: 0,
                WebkitOverflowScrolling: 'touch'
              }}
            >
              {realisedTradesLoading ? (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                  <div className="loading-spinner" style={{ margin: '0 auto' }}></div>
                  <p style={{ marginTop: '16px', color: '#6b7280' }}>Loading history positions...</p>
                </div>
              ) : realisedTradesError ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#dc3545' }}>
                  <p>Error: {realisedTradesError}</p>
                  <button
                    onClick={loadRealisedTrades}
                    style={{
                      marginTop: '16px',
                      padding: '8px 16px',
                      backgroundColor: '#3b82f6',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                  >
                    Retry
                  </button>
                </div>
              ) : realisedTrades.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
                  <p>No closed positions found.</p>
                </div>
              ) : (
                <>
                  {/* Summary */}
                  <div
                    style={{
                      marginBottom: '20px',
                      padding: '16px',
                      backgroundColor: '#f9fafb',
                      borderRadius: '8px',
                      border: '1px solid #e5e7eb'
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
                      <div>
                        <span style={{ fontSize: '12px', color: '#6b7280' }}>Total Positions: </span>
                        <strong style={{ fontSize: '14px' }}>{realisedTrades.length}</strong>
                      </div>
                      <div>
                        <span style={{ fontSize: '12px', color: '#6b7280' }}>Sum of PnL: </span>
                        <strong
                          style={{
                            fontSize: '14px',
                            color: (() => {
                              const totalPnl = realisedTrades.reduce((sum, trade) => {
                                return sum + parseFloat(trade.pnl || '0');
                              }, 0);
                              return totalPnl >= 0 ? '#28a745' : '#dc3545';
                            })()
                          }}
                        >
                          {(() => {
                            const totalPnl = realisedTrades.reduce((sum, trade) => {
                              return sum + parseFloat(trade.pnl || '0');
                            }, 0);
                            return (totalPnl >= 0 ? '+' : '') + formatBalance(totalPnl.toString()) + ' ' + (accountOverview?.currency || 'USDT');
                          })()}
                        </strong>
                      </div>
                      {accountOverview?.realisedPNL && (
                        <div>
                          <span style={{ fontSize: '12px', color: '#6b7280' }}>Account Overview Realised PnL: </span>
                          <strong
                            style={{
                              fontSize: '14px',
                              color: parseFloat(accountOverview.realisedPNL) >= 0 ? '#28a745' : '#dc3545'
                            }}
                          >
                            {(parseFloat(accountOverview.realisedPNL) >= 0 ? '+' : '') + formatBalance(accountOverview.realisedPNL) + ' ' + accountOverview.currency}
                          </strong>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Desktop table / Mobile cards */}
                  {window.innerWidth <= 768 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      {realisedTradesPaged.map((trade, index) => {
                        const rowNumber = realisedTradesStartIndex + index + 1;
                        const pnl = parseFloat(trade.pnl || '0');
                        const tradeFee = parseFloat(trade.tradeFee || '0');
                        const fundingFee = parseFloat(trade.fundingFee || '0');
                        const totalFees = tradeFee + fundingFee;
                        const closeTime = trade.closeTime ? new Date(trade.closeTime) : null;
                        return (
                          <div
                            key={trade.id || index}
                            style={{
                              border: '1px solid #e5e7eb',
                              borderRadius: '8px',
                              padding: '10px',
                              backgroundColor: 'white'
                            }}
                          >
                            <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '6px' }}>
                              {rowNumber}. {closeTime ? closeTime.toLocaleString() : 'N/A'}
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                              <strong>{trade.symbol || 'N/A'}</strong>
                              <span
                                style={{
                                  padding: '2px 6px',
                                  borderRadius: '4px',
                                  fontSize: '10px',
                                  backgroundColor: trade.type === 'CLOSE_LONG' ? '#dbeafe' : '#fce7f3',
                                  color: trade.type === 'CLOSE_LONG' ? '#1e40af' : '#9f1239'
                                }}
                              >
                                {trade.type || 'N/A'}
                              </span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px', fontSize: '12px' }}>
                              <span style={{ color: '#6b7280' }}>Trading Strategy:</span>
                              <span>{getRealisedTradesStrategyLabel(trade)}</span>
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px', fontSize: '12px' }}>
                              <div><span style={{ color: '#6b7280' }}>Size:</span> {trade.size || '0'}</div>
                              <div><span style={{ color: '#6b7280' }}>Entry:</span> {trade.openPrice || 'N/A'}</div>
                              <div><span style={{ color: '#6b7280' }}>Exit:</span> {trade.closePrice || 'N/A'}</div>
                              <div><span style={{ color: '#6b7280' }}>Fees:</span> {formatBalance(totalFees.toString())}</div>
                            </div>
                            <div
                              style={{
                                marginTop: '8px',
                                fontWeight: 700,
                                color: pnl >= 0 ? '#28a745' : '#dc3545'
                              }}
                            >
                              PnL: {(pnl >= 0 ? '+' : '') + formatBalance(pnl.toString())}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div style={{ overflowX: 'auto' }}>
                      <table
                        style={{
                          width: '100%',
                          borderCollapse: 'collapse',
                          fontSize: '12px'
                        }}
                      >
                        <thead>
                          <tr style={{ backgroundColor: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                            <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600 }}>#</th>
                            <th
                              onClick={() => handleRealisedTradesSort('closeTime')}
                              style={{ padding: '12px', textAlign: 'left', fontWeight: 600, cursor: 'pointer', userSelect: 'none' }}
                              title="Sort by Close Time"
                            >
                              Close Time {realisedTradesSortColumn === 'closeTime' ? (realisedTradesSortDirection === 'asc' ? '▲' : '▼') : ''}
                            </th>
                            <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600 }}>Symbol</th>
                            <th
                              onClick={() => handleRealisedTradesSort('type')}
                              style={{ padding: '12px', textAlign: 'left', fontWeight: 600, cursor: 'pointer', userSelect: 'none' }}
                              title="Sort by Type"
                            >
                              Type {realisedTradesSortColumn === 'type' ? (realisedTradesSortDirection === 'asc' ? '▲' : '▼') : ''}
                            </th>
                            <th style={{ padding: '12px', textAlign: 'right', fontWeight: 600 }}>Size</th>
                            <th style={{ padding: '12px', textAlign: 'right', fontWeight: 600 }}>Entry Price</th>
                            <th style={{ padding: '12px', textAlign: 'right', fontWeight: 600 }}>Exit Price</th>
                            <th
                              onClick={() => handleRealisedTradesSort('strategyName')}
                              style={{ padding: '12px', textAlign: 'left', fontWeight: 600, cursor: 'pointer', userSelect: 'none' }}
                              title="Sort by Trading Strategy"
                            >
                              Trading Strategy {realisedTradesSortColumn === 'strategyName' ? (realisedTradesSortDirection === 'asc' ? '▲' : '▼') : ''}
                            </th>
                            <th
                              onClick={() => handleRealisedTradesSort('pnl')}
                              style={{ padding: '12px', textAlign: 'right', fontWeight: 600, cursor: 'pointer', userSelect: 'none' }}
                              title="Sort by PnL"
                            >
                              PnL {realisedTradesSortColumn === 'pnl' ? (realisedTradesSortDirection === 'asc' ? '▲' : '▼') : ''}
                            </th>
                            <th style={{ padding: '12px', textAlign: 'right', fontWeight: 600 }}>Fees</th>
                          </tr>
                        </thead>
                        <tbody>
                          {realisedTradesPaged.map((trade, index) => {
                            const rowNumber = realisedTradesStartIndex + index + 1;
                            const pnl = parseFloat(trade.pnl || '0');
                            const tradeFee = parseFloat(trade.tradeFee || '0');
                            const fundingFee = parseFloat(trade.fundingFee || '0');
                            const totalFees = tradeFee + fundingFee;
                            const closeTime = trade.closeTime ? new Date(trade.closeTime) : null;

                            return (
                              <tr
                                key={trade.id || index}
                                style={{
                                  borderBottom: '1px solid #e5e7eb',
                                  backgroundColor: (realisedTradesStartIndex + index) % 2 === 0 ? 'white' : '#f9fafb'
                                }}
                              >
                                <td style={{ padding: '12px' }}>
                                  {rowNumber}
                                </td>
                                <td style={{ padding: '12px' }}>
                                  {closeTime ? closeTime.toLocaleString() : 'N/A'}
                                </td>
                                <td style={{ padding: '12px', fontWeight: 500 }}>{trade.symbol || 'N/A'}</td>
                                <td style={{ padding: '12px' }}>
                                  <span
                                    style={{
                                      padding: '4px 8px',
                                      borderRadius: '4px',
                                      fontSize: '11px',
                                      backgroundColor: trade.type === 'CLOSE_LONG' ? '#dbeafe' : '#fce7f3',
                                      color: trade.type === 'CLOSE_LONG' ? '#1e40af' : '#9f1239'
                                    }}
                                  >
                                    {trade.type || 'N/A'}
                                  </span>
                                </td>
                                <td style={{ padding: '12px', textAlign: 'right' }}>{trade.size || '0'}</td>
                                <td style={{ padding: '12px', textAlign: 'right' }}>{trade.openPrice || 'N/A'}</td>
                                <td style={{ padding: '12px', textAlign: 'right' }}>{trade.closePrice || 'N/A'}</td>
                                <td style={{ padding: '12px' }}>
                                  {getRealisedTradesStrategyLabel(trade)}
                                </td>
                                <td
                                  style={{
                                    padding: '12px',
                                    textAlign: 'right',
                                    fontWeight: 600,
                                    color: pnl >= 0 ? '#28a745' : '#dc3545'
                                  }}
                                >
                                  {(pnl >= 0 ? '+' : '') + formatBalance(pnl.toString())}
                                </td>
                                <td style={{ padding: '12px', textAlign: 'right', color: '#6b7280' }}>
                                  {formatBalance(totalFees.toString())}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* Pagination */}
                  {realisedTradesTotalPages > 1 && (
                    <div className="pagination">
                      <div className="pagination-info">
                        Showing {(realisedTradesStartIndex + 1)} -{' '}
                        {Math.min(realisedTradesStartIndex + REALISED_TRADES_PAGE_SIZE, realisedTradesSorted.length)} of {realisedTradesSorted.length}
                      </div>
                      <div className="pagination-controls">
                        <button
                          className="pagination-btn"
                          onClick={() => setRealisedTradesCurrentPage(p => Math.max(1, p - 1))}
                          disabled={realisedTradesCurrentPage === 1}
                        >
                          Previous
                        </button>
                        <button
                          className="pagination-btn"
                          onClick={() => setRealisedTradesCurrentPage(p => Math.min(realisedTradesTotalPages, p + 1))}
                          disabled={realisedTradesCurrentPage >= realisedTradesTotalPages}
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default KucoinBalances;
